'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { trackEvent } from '@/lib/track-event'
import { canonicalizeDomainInput, sanitizeHref } from '@/lib/sanitize'
import { peekVisitorId } from '@/lib/analytics/visitor-id'
import { getFirstTouchUtm } from '@/lib/analytics/first-touch-utm'
import {
  Loader2,
  KeyRound,
  ArrowRight,
  CheckCircle2,
  Sparkles,
} from 'lucide-react'

/**
 * Guided free-check launcher for the `/platform` hero (Profound-style).
 *
 *   email + domain → OTP ("Step 1: verify") → "Radar is running…" → result page.
 *
 * Steps 1-2 reuse the existing OTP endpoints (request-code / verify-code).
 * Step 4 is server-orchestrated: it POSTs the domain to /api/platform/free-check
 * which runs the audit BEHIND THE SCENES (brand-index pattern), persists a
 * teaser-locked result, emails the link, and returns a reportId. Step 5 is a
 * router.push to /platform/check/<id> — the user lands on a real result page.
 */

type State =
  | 'init'
  | 'form'
  | 'sending'
  | 'code'
  | 'verifying'
  | 'sent'
  | 'used'
  | 'capped'

interface CapInfo {
  domain?: string
  message?: string
  cachedResultUrl?: string | null
  upsellUrl?: string
}

const TOOL = 'ai-readiness'

// Delegates to the shared canonicalizer so the field accepts the same inputs
// the API will accept, and rejects the ones it won't. Previously this passed
// anything URL-parseable, so "example.com?ref=x" reached the API (and the lead
// record) with the query string still attached.
function validateDomain(value: string): boolean {
  return canonicalizeDomainInput(value) !== null
}

function friendlyError(reason: unknown): string {
  switch (reason) {
    case 'invalid':
      return 'That code is incorrect. Check your email and try again.'
    case 'expired':
      return 'That code expired. Request a new one.'
    case 'too_many_attempts':
      return 'Too many wrong attempts. Request a new code.'
    case 'rate_limit':
      return 'Too many requests. Wait a minute and try again.'
    case 'daily_cap':
      return 'You have hit the daily verification limit for this email.'
    case 'disposable_email':
      return 'Temporary email addresses are not supported. Use a real email.'
    case 'invalid_email':
      return 'That email looks invalid. Try another.'
    case 'invalid_tool':
      return 'Unsupported tool. Refresh the page.'
    case 'server_error':
      return 'Our server hit a snag. Try again in a moment.'
    default:
      return 'Something went wrong. Try again.'
  }
}

interface FreeCheckLauncherProps {
  /**
   * Primary submit label. Defaults to the /platform wording; the paid-ad
   * landing (/platform/start) overrides it so the CTA matches the ad creative.
   * Copy-only — no behavioural difference between the two surfaces.
   */
  submitLabel?: string
}

export default function FreeCheckLauncher({
  submitLabel = 'Get My Free Snapshot',
}: FreeCheckLauncherProps = {}) {
  const [state, setState] = useState<State>('init')
  const [email, setEmail] = useState('')
  const [domain, setDomain] = useState('')
  const [code, setCode] = useState('')
  // ready = the server already grants a run (paid session, founder, or a
  // still-valid free credit) → skip OTP.
  const [ready, setReady] = useState(false)
  const [cap, setCap] = useState<CapInfo | null>(null)
  // Set once the server-side run returns; enables the "view now" link on the
  // confirmation screen (the report is otherwise delivered by email).
  const [resultUrl, setResultUrl] = useState('')
  const [emailed, setEmailed] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  // Server ran the paid-user sign-in recovery for this email (uniform flag —
  // an email went out only if the account actually qualifies).
  const [recoveryInitiated, setRecoveryInitiated] = useState(false)
  // Domain arrived via ?domain= (Vector chat hands off the domain the visitor
  // already typed). Tracked so we can tell chat-sourced runs apart downstream.
  const [prefilled, setPrefilled] = useState(false)

  // Read from window.location rather than useSearchParams: the seed is only
  // ever needed after mount, so this keeps the read local to the one effect
  // that uses it and avoids adding a Suspense boundary around the launcher.
  useEffect(() => {
    const seeded = new URLSearchParams(window.location.search).get('domain')
    if (!seeded) return
    const canonical = canonicalizeDomainInput(seeded)
    if (!canonical) return
    setDomain(canonical)
    setPrefilled(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await fetch(`/api/tools/access/me?tool=${TOOL}`, {
          cache: 'no-store',
        })
        const data = await res.json().catch(() => ({}))
        if (cancelled) return
        if (data?.isPaid || data?.hasCredit) {
          setReady(true)
          setState('form')
          return
        }
        if (data?.verifiedEmail && typeof data.verifiedEmail === 'string') {
          setEmail(data.verifiedEmail)
          setState('used')
          return
        }
        setState('form')
      } catch {
        if (!cancelled) setState('form')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  async function runFreeCheck() {
    // Single choke point: all three entry paths (bypass, post-OTP, already
    // ready) land here, so canonicalizing once guarantees only a bare hostname
    // ever leaves the client — whatever is sitting in the input field.
    const target = canonicalizeDomainInput(domain)
    if (!target) {
      setErrorMsg('Enter a valid domain (e.g. example.com).')
      setState('form')
      return
    }
    // First-touch attribution for the whole journey. getFirstTouchUtm() is
    // itself consent-aware: it returns null (and clears stale data) whenever
    // analytics are not currently allowed, so a consent-denied visitor sends
    // nothing here, on the event, or to the API.
    const attribution = getFirstTouchUtm() ?? {}
    trackEvent('free_check_started', { prefilled, ...attribution })
    // Profound-style: confirm immediately and deliver the report by email. The
    // audit runs server-side; we don't block the screen on it or show the
    // result inline. A "view now" link appears once the run returns.
    setState('sent')
    setErrorMsg(null)
    try {
      const res = await fetch('/api/platform/free-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // peek, never mint: starting an audit must not create
        // attribution for a visitor who was never LLM-referred.
        body: JSON.stringify({
          url: target,
          visitorId: peekVisitorId(),
          attribution,
        }),
        keepalive: true,
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 429 && data.error === 'domain_cap') {
        setCap(data as CapInfo)
        setState('capped')
        return
      }
      if (res.status === 403) {
        // Credit no longer valid — fall back to re-verify.
        setReady(false)
        setErrorMsg('Please verify your email to run the check.')
        setState('form')
        return
      }
      if (!res.ok || !data?.resultUrl) {
        setErrorMsg(data.message || 'Could not run the check. Try again.')
        setState('form')
        return
      }
      trackEvent('free_check_completed', attribution)
      setEmailed(Boolean(data.emailed))
      setResultUrl(data.resultUrl as string)
    } catch {
      setErrorMsg('Network error. Please try again.')
      setState('form')
    }
  }

  async function requestCode() {
    setState('sending')
    setErrorMsg(null)
    try {
      const res = await fetch('/api/tools/access/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), tool: TOOL }),
      })
      const data = await res.json().catch(() => ({}))
      if (data?.reason === 'already_used') {
        setRecoveryInitiated(Boolean(data?.recoveryInitiated))
        setState('used')
        return
      }
      if (!res.ok) {
        setErrorMsg(friendlyError(data?.reason))
        setState('form')
        return
      }
      if (data?.bypass) {
        setReady(true)
        void runFreeCheck()
        return
      }
      trackEvent('platform_otp_requested', getFirstTouchUtm() ?? {})
      setState('code')
    } catch {
      setErrorMsg('Network error. Try again.')
      setState('form')
    }
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErrorMsg(null)
    if (!ready && (!email.trim() || !email.includes('@'))) {
      setErrorMsg('Enter a valid email.')
      return
    }
    if (!validateDomain(domain)) {
      setErrorMsg('Enter a valid domain (e.g. example.com).')
      return
    }
    if (ready) {
      void runFreeCheck()
      return
    }
    trackEvent('platform_email_submitted', getFirstTouchUtm() ?? {})
    void requestCode()
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!/^\d{6}$/.test(code.trim())) {
      setErrorMsg('Enter the 6-digit code from your email.')
      return
    }
    setState('verifying')
    setErrorMsg(null)
    try {
      const res = await fetch('/api/tools/access/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          code: code.trim(),
          tool: TOOL,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.ok) {
        setErrorMsg(friendlyError(data?.reason))
        setState('code')
        return
      }
      trackEvent('platform_otp_verified', getFirstTouchUtm() ?? {})
      setReady(true)
      void runFreeCheck()
    } catch {
      setErrorMsg('Network error. Try again.')
      setState('code')
    }
  }

  // ---------- render ----------
  if (state === 'init') {
    return (
      <div className='max-w-lg mx-auto mt-10 flex justify-center py-6'>
        <Loader2 size={20} className='animate-spin text-muted-foreground' />
      </div>
    )
  }

  if (state === 'sent') {
    const reportReady = Boolean(resultUrl)
    return (
      <div className='max-w-lg mx-auto mt-10'>
        <div className='rounded-2xl p-8 bg-muted/50 border border-border/60 text-center'>
          {reportReady ? (
            <CheckCircle2 size={28} className='mx-auto mb-3 text-[#22c55e]' />
          ) : (
            <Loader2
              size={28}
              className='mx-auto mb-3 animate-spin text-[#008dcc] motion-reduce:animate-none'
            />
          )}
          <p className='text-base font-semibold text-foreground mb-1'>
            {reportReady
              ? 'Your Radar scan is ready'
              : 'Preparing your Radar scan…'}
          </p>
          <p className='text-sm text-foreground/70 mb-5'>
            {!reportReady
              ? `Hang tight while Radar scans ${
                  domain
                    .trim()
                    .replace(/^https?:\/\//, '')
                    .replace(/\/+$/, '') || 'your site'
                }.`
              : emailed
                ? `We've emailed the link to ${email}. Open it any time, or view it now.`
                : 'Your audit is ready.'}
          </p>
          {reportReady && (
            <a
              href={sanitizeHref(resultUrl) ?? '/platform'}
              className='inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full text-sm font-bold text-white bg-[#008dcc] hover:bg-[#008dcc]/90 transition-colors'
            >
              View your result
              <ArrowRight size={14} />
            </a>
          )}
        </div>
      </div>
    )
  }

  if (state === 'capped') {
    return (
      <div className='max-w-lg mx-auto mt-10'>
        <div className='rounded-2xl p-8 bg-muted/50 border border-border/60 text-center'>
          <Sparkles size={24} className='mx-auto mb-3 text-[#8b5cf6]' />
          <p className='text-base font-semibold text-foreground mb-1'>
            Free checks used for {cap?.domain ?? 'this domain'}
          </p>
          <p className='text-sm text-foreground/70 mb-5'>
            This domain has reached its free limit. Unlock unlimited re-runs and
            all 13 tools with Radar.
          </p>
          <a
            href={sanitizeHref(cap?.upsellUrl ?? '') ?? '/platform#pricing'}
            className='inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full text-sm font-bold text-white bg-[#008dcc] hover:bg-[#008dcc]/90 transition-colors'
          >
            See Radar plans
            <ArrowRight size={14} />
          </a>
          {cap?.cachedResultUrl && (
            <a
              href={sanitizeHref(cap.cachedResultUrl) ?? '/platform'}
              className='block text-xs font-semibold text-[#008dcc] hover:underline mt-3'
            >
              View the existing report for {cap.domain}
            </a>
          )}
        </div>
      </div>
    )
  }

  if (state === 'used') {
    return (
      <div className='max-w-lg mx-auto mt-10'>
        <div className='rounded-2xl p-8 bg-muted/50 border border-border/60 text-center'>
          <Sparkles size={24} className='mx-auto mb-3 text-[#8b5cf6]' />
          <p className='text-base font-semibold text-foreground mb-1'>
            You have used your free check
          </p>
          {recoveryInitiated && (
            <p className='text-sm text-foreground/80 mb-4'>
              If this email has Radar credits, purchases, or an active
              subscription, we&apos;ll send a sign-in link. Please check your
              inbox and spam folder.
            </p>
          )}
          <p className='text-sm text-foreground/70 mb-5'>
            Unlock the full report for $5, or go Pro at $199/mo for unlimited
            audits across every tool.
          </p>
          <a
            href='/platform#pricing'
            className='inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full text-sm font-bold text-white bg-[#008dcc] hover:bg-[#008dcc]/90 transition-colors'
          >
            See pricing
            <ArrowRight size={14} />
          </a>
          <p className='text-xs text-muted-foreground mt-4'>
            Already purchased?{' '}
            <a
              href='/platform/sign-in'
              className='text-[#008dcc] font-medium hover:underline'
            >
              Sign in
            </a>
          </p>
        </div>
      </div>
    )
  }

  if (state === 'code' || state === 'verifying') {
    return (
      <div className='max-w-lg mx-auto mt-10'>
        <form
          onSubmit={handleCodeSubmit}
          className='rounded-2xl p-6 bg-muted/50 border border-border/60 text-left'
        >
          <p className='text-[11px] font-bold uppercase tracking-widest text-[#008dcc] mb-2 leading-none'>
            Step 1 of 2 &mdash; verify your email
          </p>
          <p className='text-sm text-foreground/75 mb-4'>
            We sent a 6-digit code to {email} so we can send your report. Enter
            it to start your free check.
          </p>
          <div className='flex flex-col gap-3 sm:flex-row'>
            <div className='relative w-full sm:flex-1'>
              <KeyRound
                size={16}
                className='absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground'
              />
              <input
                type='text'
                inputMode='numeric'
                autoComplete='one-time-code'
                maxLength={6}
                value={code}
                onChange={e => {
                  setCode(e.target.value.replace(/\D/g, ''))
                  if (errorMsg) setErrorMsg(null)
                }}
                placeholder='123456'
                autoFocus
                disabled={state === 'verifying'}
                className='w-full h-14 pl-11 pr-4 rounded-full border border-border/60 bg-background text-foreground placeholder:text-muted-foreground text-base tracking-[6px] font-mono focus:ring-2 focus:ring-[#008dcc]/50 focus:border-[#008dcc]'
              />
            </div>
            <button
              type='submit'
              disabled={state === 'verifying' || !/^\d{6}$/.test(code.trim())}
              className='w-full sm:w-auto h-14 px-6 rounded-full text-base font-bold text-white bg-[#008dcc] hover:bg-[#008dcc]/90 transition-colors whitespace-nowrap disabled:opacity-40'
            >
              {state === 'verifying' ? (
                <Loader2 size={18} className='animate-spin' />
              ) : (
                'Run my check'
              )}
            </button>
          </div>
          {errorMsg && (
            <p className='text-xs text-[#F90B8A] mt-3'>{errorMsg}</p>
          )}
          <button
            type='button'
            onClick={() => {
              setCode('')
              setState('form')
            }}
            className='text-xs text-muted-foreground hover:text-foreground mt-3 transition-colors'
          >
            Use a different email
          </button>
        </form>
      </div>
    )
  }

  // 'form' (and 'sending' — same UI, the button shows a spinner)
  const sending = state === 'sending'
  return (
    <div className='max-w-lg mx-auto mt-10'>
      <form onSubmit={handleFormSubmit} className='space-y-3'>
        {!ready && (
          <input
            type='email'
            value={email}
            onChange={e => {
              setEmail(e.target.value)
              if (errorMsg) setErrorMsg(null)
            }}
            placeholder='your@company.com'
            required
            disabled={sending}
            className='w-full h-14 px-5 rounded-full border border-border/60 bg-muted text-foreground placeholder:text-muted-foreground text-base focus:ring-2 focus:ring-[#008dcc]/50 focus:border-[#008dcc]'
          />
        )}
        <div className='flex flex-col gap-3 sm:flex-row'>
          <input
            type='text'
            value={domain}
            onChange={e => {
              setDomain(e.target.value)
              if (errorMsg) setErrorMsg(null)
            }}
            placeholder='yourdomain.com'
            required
            disabled={sending}
            className='w-full sm:flex-1 h-14 px-5 rounded-full border border-border/60 bg-muted text-foreground placeholder:text-muted-foreground text-base focus:ring-2 focus:ring-[#008dcc]/50 focus:border-[#008dcc]'
          />
          {/* Standard marketing CTA: the `action` variant's magenta fill,
              lift, and glow — same primary treatment as every other
              conversion button on the site. */}
          <Button
            type='submit'
            variant='action'
            size='lg'
            disabled={sending}
            className='w-full sm:w-auto h-14 px-8 whitespace-nowrap'
          >
            {sending ? (
              <Loader2 size={18} className='animate-spin' />
            ) : (
              submitLabel
            )}
          </Button>
        </div>
        {errorMsg && <p className='text-xs text-[#F90B8A]'>{errorMsg}</p>}
        {!ready && (
          <p className='text-xs text-muted-foreground'>
            We use your email to send results and the occasional product update.
            Unsubscribe any time.
          </p>
        )}
      </form>
    </div>
  )
}
